import { lazy, Suspense, Component } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { useState, useEffect, createContext, useContext } from "react";
import { fetchProjectConfig } from "./config/projectConfig";
import { ThemeProvider } from "./context/ThemeProvider";
import ThemeRouteScope from "./context/ThemeRouteScope";
import logger from "./lib/logger";

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-primary text-2xl">Loading...</div>
  </div>
);

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    logger.error("ErrorBoundary caught:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", textAlign: "center" }}>
          <h1 style={{ fontSize: "1.5rem", fontWeight: "bold", marginBottom: "0.5rem" }}>Something went wrong</h1>
          <p style={{ color: "#6b7280", marginBottom: "1rem" }}>An unexpected error occurred. Please reload the page.</p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: "0.5rem 1.25rem", background: "#2563eb", color: "#fff", border: "none", borderRadius: "0.375rem", cursor: "pointer" }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Pages — lazy-loaded for route-level code splitting
const LandingPage          = lazy(() => import("./pages/LandingPage"));
const LoginPage            = lazy(() => import("./pages/LoginPage"));
const RegisterPage         = lazy(() => import("./pages/RegisterPage"));
const KzoolAdminLoginPage  = lazy(() => import("./pages/KzoolAdminLoginPage"));
const AdminDashboardLayout = lazy(() => import("./layouts/AdminDashboardLayout"));
const OverviewPage         = lazy(() => import("./pages/admin/OverviewPage"));
const CodesPage            = lazy(() => import("./pages/admin/CodesPage"));
const TenantsPage          = lazy(() => import("./pages/admin/TenantsPage"));
const BillsPage            = lazy(() => import("./pages/admin/BillsPage"));
const AdminSettingsPage    = lazy(() => import("./pages/admin/SettingsPage"));
const DashboardLayout      = lazy(() => import("./layouts/DashboardLayout"));
const DashboardPage        = lazy(() => import("./pages/DashboardPage"));
const GuestsPage           = lazy(() => import("./pages/GuestsPage"));
const GuestDetailPage      = lazy(() => import("./pages/GuestDetailPage"));
const ReservationsPage     = lazy(() => import("./pages/ReservationsPage"));
const RoomsPage            = lazy(() => import("./pages/RoomsPage"));
const StaffPage            = lazy(() => import("./pages/StaffPage"));
const ActivitiesPage       = lazy(() => import("./pages/ActivitiesPage"));
const CollaboratorsPage    = lazy(() => import("./pages/CollaboratorsPage"));
const RevenueSimPage       = lazy(() => import("./pages/RevenueSimPage"));
const ReportsPage          = lazy(() => import("./pages/ReportsPage"));
const SettingsPage         = lazy(() => import("./pages/SettingsPage"));
const AccessDeniedPage     = lazy(() => import("./pages/AccessDeniedPage"));
const ForgotPasswordPage      = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage       = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage         = lazy(() => import("./pages/VerifyEmailPage"));
const AcceptInvitationPage    = lazy(() => import("./pages/AcceptInvitationPage"));
import { canAccessRoute, hasFullAccess } from "./utils/permissions";

// Import centralized API configuration
import { APP_URL, SITE_URL } from "./config";
import apiClient from "./services/api";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Admin session lives in its own HttpOnly cookie (kzool_admin_token) and can't be
      // probed via /auth/me. Re-validate via /kzool-admin/me — this also re-arms the
      // CSRF token in sessionStorage (which is per-tab, so a fresh tab / restored session
      // would otherwise have a stale-or-missing X-CSRF-Token → 403 on every admin write).
      if (sessionStorage.getItem("isKzoolAdmin") === "true") {
        try {
          const { user: adminUser, csrf_token } = (await apiClient.get("/kzool-admin/me")).data;
          if (csrf_token) sessionStorage.setItem("csrf_token", csrf_token);
          setUser(adminUser);
          sessionStorage.setItem("user", JSON.stringify(adminUser));
        } catch (error) {
          if (error.response?.status !== 401) logger.error("Admin auth error:", error);
          sessionStorage.removeItem("user");
          sessionStorage.removeItem("csrf_token");
          sessionStorage.removeItem("isKzoolAdmin");
          setUser(null);
        }
        setLoading(false);
        return;
      }

      // Tenant session: the access_token cookie is sent automatically. Ask the server
      // who we are; it also returns the CSRF token to re-arm writes after a refresh.
      // On 401 (no/expired cookie) there's simply no session.
      try {
        const { user: userData, csrf_token } = (await apiClient.get("/auth/me")).data;
        if (csrf_token) sessionStorage.setItem("csrf_token", csrf_token);
        setUser(userData);
        sessionStorage.setItem("user", JSON.stringify(userData));
      } catch (error) {
        if (error.response?.status !== 401) logger.error("Auth error:", error);
        sessionStorage.removeItem("user");
        sessionStorage.removeItem("csrf_token");
        setUser(null);
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  // Persist the session's display identity + CSRF token from a login/register response.
  const persistSession = ({ user: userData, csrf_token }) => {
    if (csrf_token) sessionStorage.setItem("csrf_token", csrf_token);
    sessionStorage.setItem("user", JSON.stringify(userData));
    sessionStorage.removeItem("isKzoolAdmin");
    setUser(userData);
    return userData;
  };

  const login = async (email, password) => {
    // Server sets the HttpOnly auth cookie; body carries the user + CSRF token.
    const response = await apiClient.post("/auth/login", { email, password });
    return persistSession(response.data);
  };

  const register = async (email, password, full_name, property_name, kzool_code) => {
    const response = await apiClient.post("/auth/register", {
      email,
      password,
      full_name,
      property_name,
      // Omit the code entirely when blank so the backend takes the self-signup path.
      ...(kzool_code ? { kzool_code } : {}),
    });
    // Self-signup (no code) → pending email verification, no session yet.
    if (response.data?.pending) {
      return { pending: true, email: response.data.email };
    }
    return persistSession(response.data);
  };

  const logout = async () => {
    // Server clears the HttpOnly cookie (the client can't). Best-effort — clear UI state
    // regardless so logout always visually succeeds.
    try {
      await apiClient.post("/auth/logout");
    } catch (error) {
      logger.error("Logout error:", error);
    }
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("csrf_token");
    sessionStorage.removeItem("isKzoolAdmin");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route for KZOOL Admin
const KzoolAdminProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary text-2xl">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/kzool-admin/login" replace />;
  }
  
  if (user.role !== "kzool_owner") {
    return <Navigate to="/kzool-admin/login" replace />;
  }
  
  return children;
};

// Protected Route for Tenant Users with Role-Based Access
const TenantProtectedRoute = ({ children, checkPath = true, requireFullAccess = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary text-2xl">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect KZOOL admin away from tenant routes
  if (user.role === "kzool_owner") {
    return <Navigate to="/kzool-admin/overview" replace />;
  }
  
  // Owner/Admin-only detail routes (not in the nav, so canAccessRoute can't gate them)
  if (requireFullAccess && !hasFullAccess(user.role)) {
    return <AccessDeniedPage />;
  }

  // Check if user has access to this route
  if (checkPath && !canAccessRoute(user, location.pathname)) {
    return <AccessDeniedPage />;
  }

  return children;
};

// Redirect app routes rendered client-side on the marketing domain (e.g. an old
// tab loaded before a deploy, or any in-app navigation) to the app subdomain.
// Complements the nginx 301, which only catches full server requests.
const MARKETING_HOSTS = ["kzool.com", "www.kzool.com"];
const APP_HOSTS = ["app.kzool.com"];
// App routes belong on app.kzool.com; the platform admin belongs on kzool.com.
// kzool-admin is intentionally NOT in APP_ROUTE_RE.
const APP_ROUTE_RE = /^\/(login|register|forgot-password|reset-password|verify-email|accept-invitation|dashboard)(\/|$)/;
const ADMIN_ROUTE_RE = /^\/kzool-admin(\/|$)/;

// Bidirectional domain guard (complements the nginx 301s for client-side navigation
// and stale tabs). Keeps tenant auth (app.kzool.com) and platform admin auth
// (kzool.com) on separate origins so their sessions never mix.
function DomainGuard() {
  const location = useLocation();
  useEffect(() => {
    const host = window.location.hostname;
    const path = location.pathname;
    // Marketing domain must not serve app routes -> app subdomain
    if (MARKETING_HOSTS.includes(host) && APP_ROUTE_RE.test(path)) {
      window.location.replace(`${APP_URL}${path}${location.search}`);
      return;
    }
    // App subdomain must not serve the platform admin -> marketing domain
    if (APP_HOSTS.includes(host) && ADMIN_ROUTE_RE.test(path)) {
      window.location.replace(`${SITE_URL}${path}${location.search}`);
    }
  }, [location.pathname, location.search]);
  return null;
}

function App() {
  // Fetch project config on app start
  useEffect(() => {
    fetchProjectConfig();
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <DomainGuard />
          <ThemeRouteScope />
          <Suspense fallback={<PageLoader />}>
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/accept-invitation/:token" element={<AcceptInvitationPage />} />

          {/* KZOOL Admin Routes */}
          <Route path="/kzool-admin/login" element={<KzoolAdminLoginPage />} />
          <Route
            path="/kzool-admin"
            element={
              <KzoolAdminProtectedRoute>
                <AdminDashboardLayout />
              </KzoolAdminProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/kzool-admin/overview" replace />} />
            <Route path="overview" element={<OverviewPage />} />
            <Route path="dashboard" element={<Navigate to="/kzool-admin/overview" replace />} />
            <Route path="codes" element={<CodesPage />} />
            <Route path="tenants" element={<TenantsPage />} />
            <Route path="bills" element={<BillsPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
          </Route>
          
          {/* Protected Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              <TenantProtectedRoute checkPath={false}>
                <DashboardLayout />
              </TenantProtectedRoute>
            }
          >
            <Route index element={<TenantProtectedRoute><DashboardPage /></TenantProtectedRoute>} />
            <Route path="guests" element={<TenantProtectedRoute><GuestsPage /></TenantProtectedRoute>} />
            <Route path="guests/:id" element={<TenantProtectedRoute requireFullAccess><GuestDetailPage /></TenantProtectedRoute>} />
            <Route path="reservations" element={<TenantProtectedRoute><ReservationsPage /></TenantProtectedRoute>} />
            <Route path="rooms" element={<TenantProtectedRoute><RoomsPage /></TenantProtectedRoute>} />
            <Route path="rooms/:roomTypeId" element={<TenantProtectedRoute><RoomsPage /></TenantProtectedRoute>} />
            <Route path="staff" element={<TenantProtectedRoute><StaffPage /></TenantProtectedRoute>} />
            <Route path="activities" element={<TenantProtectedRoute><ActivitiesPage /></TenantProtectedRoute>} />
            <Route path="collaborators" element={<TenantProtectedRoute><CollaboratorsPage /></TenantProtectedRoute>} />
            <Route path="revenue" element={<TenantProtectedRoute><RevenueSimPage /></TenantProtectedRoute>} />
            <Route path="reports" element={<TenantProtectedRoute><ReportsPage /></TenantProtectedRoute>} />
            <Route path="settings" element={<TenantProtectedRoute><SettingsPage /></TenantProtectedRoute>} />
          </Route>
          </Routes>
          </Suspense>
          <Toaster position="bottom-right" duration={3000} />
        </BrowserRouter>
      </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
